import {
  AfterViewInit,
  Directive,
  ElementRef,
  Input,
  OnChanges,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Sets `aria-disabled` on the host, overriding a component's own binding.
 *
 * `MatMenuItem` binds `[attr.aria-disabled]` to its own `disabled` input. An
 * item that must stay *reachable* has to leave `disabled` false, because the
 * menu's `FocusKeyManager` skips disabled items and a keyboard user then never
 * lands on it at all - so it would always announce `aria-disabled="false"`,
 * exactly when it needs to announce the opposite.
 *
 * Writing the attribute in `ngAfterViewInit` lands after Angular has applied
 * that host binding on first render, and `ngOnChanges` covers later toggles
 * (`MatMenuItem` does not rewrite the attribute unless its own `disabled`
 * changes, which it does not here). See EHQ-243.
 */
@Directive({
  selector: '[appAriaDisabled]',
  standalone: true,
})
export class AriaDisabledDirective implements AfterViewInit, OnChanges {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);

  @Input({ required: true, alias: 'appAriaDisabled' }) disabled = false;

  ngAfterViewInit(): void {
    this.apply();
  }

  ngOnChanges(): void {
    this.apply();
  }

  private apply(): void {
    // Always write it, rather than removing when false: every other
    // mat-menu-item renders aria-disabled="false" from MatMenuItem's own host
    // binding, and matching that keeps the DOM consistent.
    this.renderer.setAttribute(
      this.elementRef.nativeElement,
      'aria-disabled',
      this.disabled ? 'true' : 'false',
    );
  }
}
